import { useCallback, useMemo } from "react";
import { Button, ProgressBar, Spinner, Table } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import DeviceLogApi from "./DeviceLogApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFileWaveform } from "@fortawesome/free-solid-svg-icons";

function DeviceLog({ reports, isReading, currentChunk, totalChunks, handleRead, handleClear, data }) {
    const logApi = useMemo(() => reports && new DeviceLogApi(reports), [reports]);
    const valuableRecords = useMemo(() => logApi && logApi.valuableRecords(), [logApi]);

    const { t } = useTranslation();

    function getTimeText(record) {
        const sec = record.time2s * 2;

        // Format to HH:MM:SS
        const hours = Math.floor(sec / 3600);
        const minutes = Math.floor((sec - (hours * 3600)) / 60);
        const seconds = sec - (hours * 3600) - (minutes * 60);
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    function getModeText(record) {
        const modeText = `${DeviceLogApi.intCodeToCharCode(DeviceLogApi.MAIN_STATES, record.mainState)}/${DeviceLogApi.intCodeToCharCode(DeviceLogApi.CONTROL_MODES, record.controlMode)}/${DeviceLogApi.intCodeToCharCode(DeviceLogApi.HEATER_STATES, record.heaterState)}`;
        const chargeText = `${DeviceLogApi.intCodeToCharCode(DeviceLogApi.CHARGE_PLUGGED_STATES, record.chargePlugged)}/${DeviceLogApi.intCodeToCharCode(DeviceLogApi.CHARGE_END_STATES, record.chargeEnd)}`;
        return `${t('device.log.mode')}: ${modeText} ${t('device.log.charge')}: ${chargeText}`;
    }

    function getPowerText(record) {
        let items = [];

        if (record.voltage !== null) {
            items.push(record.voltage.toFixed(2) + t('V'));
        }

        if (record.current !== null) {
            items.push(record.current.toFixed(2) + t('A'));
        }

        return items.join(", ");
    }

    function getTemperatureText(record) {
        let items = [];

        if (record.mcuTemp !== null) {
            items.push(t('device.log.mcuTemp') + `=${record.mcuTemp}°C`);
        }

        if (record.sensTemp !== null) {
            items.push(t('device.log.sensTemp') + `=${record.sensTemp}°C`);
        }

        return items.join(", ");
    }

    const filenameSuffix = useMemo(() => {
        let suffix = '_';

        // Add YYYY-MM-DD_HH-MM-SS (local date)
        const now = new Date(); // local date & time
        const pad = n => n.toString().padStart(2, '0');

        const YYYY = now.getFullYear();
        const MM = pad(now.getMonth() + 1);
        const DD = pad(now.getDate());
        const HH = pad(now.getHours());
        const mm = pad(now.getMinutes());
        const ss = pad(now.getSeconds());

        suffix += `${YYYY}-${MM}-${DD}_${HH}-${mm}-${ss}`;

        // Add systemId if present
        if (data?.deviceInfo?.systemId) {
            const id = data.deviceInfo.systemId;

            // remove all dashes except two ones
            let hyphenCount = 0;
            const idWithoutDashes = id.replace(/-/g, (match) => {
                hyphenCount++;
                return hyphenCount <= 2 ? '-' : '';
            });

            suffix += '_ID@' + idWithoutDashes;
        }

        // Add firmware revision string if present
        if (data?.deviceInfo?.firmwareRevisionString) {
            suffix += '_FW@' + data.deviceInfo.firmwareRevisionString;
        }

        // Add hardware revision string if present
        // NOTE: skip since this data is indirectly present in FW version
        // if (data?.deviceInfo?.hardwareRevisionString) {
        //     suffix += '_HW@' + data.deviceInfo.hardwareRevisionString;
        // }

        return suffix;
    }, [data]);

    const downloadRaw = useCallback(() => {
        // reports are Uint8Array actually
        // download it as .bin file
        const blob = new Blob([reports], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `log${filenameSuffix}.bin`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [reports, filenameSuffix]);

    const exportXlsx = useCallback(() => {
        // Export records
        const excelBuffer = logApi.exportToXlsx({ pretty: true });

        // Create a download link for the XLSX file
        const fileName = `log${filenameSuffix}.xlsx`;
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [logApi, filenameSuffix]);

    return (
        <div className="my-4">
            {isReading ? (
                <div>
                    <div className="fst-italic"><Trans>device.log.reading</Trans></div>

                    {(currentChunk !== null && totalChunks !== null) ? (
                        <ProgressBar
                            animated
                            striped
                            now={(currentChunk / totalChunks) * 100}
                            label={`${currentChunk}/${totalChunks}`}
                        />
                    ) : (
                        <Spinner animation="grow" />
                    )}
                </div>
            ) : (
                <div className="d-flex">
                    <Button
                        variant="primary"
                        onClick={handleRead}
                        className="me-2"
                    >
                        <Trans>device.log.read</Trans>
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleClear}
                    >
                        <Trans>device.log.clear</Trans>
                    </Button>
                </div>
            )}

            {logApi && (
                <Table striped bordered hover className="mt-3">
                    <thead>
                        <tr>
                            <th><Trans>device.log.time</Trans></th>
                            <th><Trans>device.log.state</Trans></th>
                            <th><Trans>device.log.power</Trans></th>
                            <th><Trans>device.log.temperature</Trans></th>
                        </tr>
                    </thead>

                    <tbody>
                        {valuableRecords.length > 0 ? (
                            valuableRecords.map((record, index) => (
                                <tr key={index}>
                                    <td>{getTimeText(record)}</td>
                                    <td>{getModeText(record)}</td>
                                    <td>{getPowerText(record)}</td>
                                    <td>{getTemperatureText(record)}</td>
                                </tr>
                            ))) : (
                            <tr>
                                <td colSpan="4"><Trans>device.log.noRecords</Trans></td>
                            </tr>
                        )}
                    </tbody>

                    {valuableRecords.length > 0 && (
                        <tfoot>
                            <tr>
                                <td colSpan="4">
                                    <div className="d-flex justify-content-end">
                                        <Button
                                            variant="secondary"
                                            onClick={downloadRaw}
                                            className="me-2"
                                        >
                                            <FontAwesomeIcon icon={faFileWaveform} /> <Trans>device.log.raw</Trans>
                                        </Button>

                                        <Button
                                            variant="primary"
                                            onClick={exportXlsx}
                                        >
                                            <FontAwesomeIcon icon={faFileExcel} /> <Trans>device.log.export</Trans>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </Table>
            )}
        </div>
    );
}

export default DeviceLog;
